import { AlertTriangleIcon } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

import { OrderColumnHeader } from '@/views/Order/components/table/ColumnHeader';
import { formatShortDate } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import {
  PickingJobSegmentBadge,
  PickingJobStatusBadge,
} from './PickingJobStatusBadge';
import {
  COLUMN_IDS,
  STUCK_THRESHOLD_DAYS,
  destinationLabel,
  pickedRatio,
} from './pickingJobFilters';
import type { PickingJobRow } from './types';

export const pickingJobColumns: ColumnDef<PickingJobRow>[] = [
  {
    id: COLUMN_IDS.job,
    accessorFn: (row) => row.id,
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Picking:columnJob')} />
    ),
    cell: ({ row }) => (
      <span className="tw:font-mono tw:text-xs" title={row.original.id}>
        {row.original.id.slice(0, 8)}
      </span>
    ),
  },
  {
    id: COLUMN_IDS.segment,
    accessorFn: (row) => row.segment,
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnSegment')}
      />
    ),
    cell: ({ row }) => (
      <PickingJobSegmentBadge segment={row.original.segment} />
    ),
  },
  {
    id: COLUMN_IDS.status,
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnStatus')}
      />
    ),
    cell: ({ row }) => <PickingJobStatusBadge status={row.original.status} />,
  },
  {
    id: COLUMN_IDS.warehouse,
    accessorFn: (row) => row.warehouseName,
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnWarehouse')}
      />
    ),
    cell: ({ row }) => row.original.warehouseName,
  },
  {
    id: COLUMN_IDS.destination,
    accessorFn: (row) => destinationLabel(row),
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnDestination')}
      />
    ),
    cell: ({ row }) => destinationLabel(row.original),
  },
  {
    id: COLUMN_IDS.pickupDate,
    accessorFn: (row) => row.carrier_pickup_date,
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnPickupDate')}
      />
    ),
    cell: ({ row }) =>
      row.original.carrier_pickup_date
        ? formatShortDate(row.original.carrier_pickup_date)
        : '-',
  },
  {
    id: COLUMN_IDS.age,
    accessorFn: (row) => row.ageInDays,
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Picking:columnAge')} />
    ),
    cell: ({ row }) => {
      const { ageInDays } = row.original;
      if (ageInDays === null) return '-';
      // A pickup date still ahead is not an age.
      if (ageInDays < 0) {
        return i18n.t('Picking:ageInFuture', { days: -ageInDays });
      }
      // Same comparison as the "Older than" age filter.
      const isStuck = ageInDays > STUCK_THRESHOLD_DAYS;
      return (
        <span
          className={
            isStuck
              ? 'tw:flex tw:items-center tw:gap-1 tw:font-medium tw:text-destructive'
              : undefined
          }
        >
          {isStuck && <AlertTriangleIcon className="tw:size-4 tw:shrink-0" />}
          {i18n.t('Picking:ageInDays', { days: ageInDays })}
        </span>
      );
    },
  },
  {
    id: COLUMN_IDS.picked,
    accessorFn: pickedRatio,
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Picking:columnPicked')}
      />
    ),
    cell: ({ row }) =>
      `${row.original.total_amount_picked} / ${row.original.total_amount_ordered}`,
  },
];
